import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import FilterListIcon from "@mui/icons-material/FilterList";
import UndoOutlinedIcon from "@mui/icons-material/UndoOutlined";
import TaskAltOutlinedIcon from "@mui/icons-material/TaskAltOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { toast } from "react-toastify";
import ConfirmationDialog from "../../Components/ConfirmationDialog";
import SuperAdminPageHeader from "./SuperAdminPageHeader";
import SuperAdminLocationSelects from "./SuperAdminLocationSelects";
import { useLocationCascading } from "./hooks/useLocationCascading";
import { useAdminPanel } from "./hooks/useAdminPanel";
import {
  formatDisplayDateTime,
  getDeletedAccounts,
  locationFilterParams,
  updateDeletedAccountStatus,
} from "../../api/superAdminApi";

const ROWS_PER_PAGE_OPTIONS = [5, 10, 20, 25, 50];

const TABLE_HEADERS = [
  "S.No",
  "Name",
  "Email",
  "Reason",
  "Account status",
  "Created at",
  "Scheduled deletion",
  "Action",
];

const getAccountUserId = (row) =>
  row.user_id ?? row.account_user_id ?? row.target_user_id ?? row.venue_user_id ?? row.account_id;

const getAccountStatus = (row) => row.account_status ?? row.status ?? "";

const getEditAction = (status) => {
  if (status === "DeleteRequested") return "reject";
  if (status === "InActive") return "restore";
  return null;
};

const getDeleteAction = (status) => {
  if (status === "DeleteRequested") return "approve";
  if (status === "InActive" || status === "DeleteApproved") return "delete";
  return null;
};

const getDeleteTooltip = (status) => {
  if (status === "DeleteRequested") return "Approve deletion (2-day schedule)";
  if (status === "DeleteApproved") return "Permanently delete account now";
  if (status === "InActive") return "Permanently delete account";
  return "";
};

const getActionButtonProps = (action, status) => {
  switch (action) {
    case "reject":
      return {
        tooltip: "Reject request (restore to Active)",
        Icon: UndoOutlinedIcon,
        color: "primary",
        ariaLabel: "Reject deletion request",
      };
    case "restore":
      return {
        tooltip: "Restore account to Active",
        Icon: UndoOutlinedIcon,
        color: "primary",
        ariaLabel: "Restore account",
      };
    case "approve":
      return {
        tooltip: "Approve deletion (2-day schedule)",
        Icon: TaskAltOutlinedIcon,
        color: "success",
        ariaLabel: "Approve deletion",
      };
    case "delete":
      return {
        tooltip: getDeleteTooltip(status),
        Icon: DeleteOutlineIcon,
        color: "error",
        ariaLabel: "Delete account permanently",
      };
    default:
      return null;
  }
};

const getConfirmCopy = (action, row) => {
  const name = row?.name || "this account";
  switch (action) {
    case "reject":
      return {
        title: "Reject deletion request",
        message: `Reject the deletion request for ${name}? The account will be restored to Active.`,
      };
    case "restore":
      return {
        title: "Restore account",
        message: `Restore ${name} to Active?`,
      };
    case "approve":
      return {
        title: "Approve deletion",
        message: `Approve deletion for ${name}? The account will be scheduled for deletion in 2 days.`,
      };
    case "delete":
      return {
        title: "Delete account permanently",
        message: `Permanently delete ${name}? This cannot be undone.`,
      };
    default:
      return { title: "Confirm action", message: "Are you sure you want to continue?" };
  }
};

export default function VMSuperAdminDeletedAccounts() {
  const { adminScope, locationLocked } = useAdminPanel();
  const loc = useLocationCascading({ includeVenue: false });
  const {
    countryId,
    stateId,
    cityId,
    countryLabel,
    stateLabel,
    cityLabel,
  } = loc;
  const [rows, setRows] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [searchInput, setSearchInput] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  const [pendingRow, setPendingRow] = useState(null);

  const listRequestSeq = useRef(0);

  const locationParams = useMemo(
    () =>
      locationFilterParams(
        { countryId, stateId, cityId, countryLabel, stateLabel, cityLabel },
        adminScope,
        { locked: locationLocked }
      ),
    [countryId, stateId, cityId, countryLabel, stateLabel, cityLabel, adminScope, locationLocked]
  );

  const buildFilters = useCallback(
    (pageOverride) => {
      const params = {
        page: pageOverride !== undefined ? pageOverride : page,
        no_of_rows: rowsPerPage,
        ...locationParams,
      };
      if (appliedSearch.trim()) params.search = appliedSearch.trim();
      return params;
    },
    [page, rowsPerPage, appliedSearch, locationParams]
  );

  const fetchList = useCallback(async (pageOverride) => {
    const seq = ++listRequestSeq.current;
    setLoading(true);
    try {
      const res = await getDeletedAccounts(buildFilters(pageOverride));
      if (seq !== listRequestSeq.current) return;
      const body = res.data;
      setRows(Array.isArray(body?.data) ? body.data : []);
      setTotalRecords(body?.totalCount ?? 0);
    } catch {
      if (seq !== listRequestSeq.current) return;
      setRows([]);
      setTotalRecords(0);
      toast.info("Deleted accounts API unavailable.");
    } finally {
      if (seq === listRequestSeq.current) setLoading(false);
    }
  }, [buildFilters]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  const handleSearch = () => {
    setAppliedSearch(searchInput.trim());
    setPage(0);
    setFiltersOpen(false);
  };

  const handleReset = () => {
    setSearchInput("");
    setAppliedSearch("");
    setPage(0);
    if (!locationLocked) {
      loc.resetLocation();
    }
  };

  const runAction = async (row, action) => {
    const accountUserId = getAccountUserId(row);
    if (!accountUserId) {
      toast.error("This row has no venue user id.");
      return;
    }

    setActionLoading(true);
    try {
      const res = await updateDeletedAccountStatus(accountUserId, action);
      const body = res.data;

      if (body?.status_code === 200) {
        toast.success(body.message || "Action completed successfully.");
        setConfirmOpen(false);
        setPendingAction(null);
        setPendingRow(null);
        fetchList();
      } else {
        toast.error(body?.message || "Action failed.");
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || "Action failed.");
    } finally {
      setActionLoading(false);
    }
  };

  const openConfirm = (row, action) => {
    setPendingRow(row);
    setPendingAction(action);
    setConfirmOpen(true);
  };

  const handleEditClick = (row) => {
    const action = getEditAction(getAccountStatus(row));
    if (!action) return;
    openConfirm(row, action);
  };

  const handleDeleteClick = (row) => {
    const action = getDeleteAction(getAccountStatus(row));
    if (!action) return;
    openConfirm(row, action);
  };

  const confirmCopy = getConfirmCopy(pendingAction, pendingRow);

  return (
    <Box>
      <SuperAdminPageHeader
        title="Manage Deleted Account Requests"
        actions={
          <Button
            variant={filtersOpen ? "contained" : "outlined"}
            startIcon={<FilterListIcon />}
            onClick={() => setFiltersOpen((o) => !o)}
          >
            Filters
          </Button>
        }
      />

      {filtersOpen ? (
        <Paper variant="outlined" sx={{ p: 2.5, mb: 2 }}>
          <Stack spacing={2.5}>
            <Grid container spacing={2}>
              <SuperAdminLocationSelects
                {...loc}
                locationLocked={locationLocked}
                lockedScope={adminScope}
                menuMinWidth={280}
                itemProps={{ xs: 12, sm: 6, md: 3 }}
              />
            </Grid>
            <Divider />
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={8}>
                <TextField
                  fullWidth
                  size="small"
                  label="Search"
                  placeholder="Name or email"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <Stack direction="row" spacing={1}>
                  <Button variant="contained" onClick={handleSearch} disabled={loading || actionLoading} fullWidth>
                    {loading ? <CircularProgress size={22} color="inherit" /> : "Search"}
                  </Button>
                  <Button variant="outlined" onClick={handleReset} disabled={loading || actionLoading}>
                    Reset
                  </Button>
                </Stack>
              </Grid>
            </Grid>
          </Stack>
        </Paper>
      ) : null}

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: "primary.main" }}>
              {TABLE_HEADERS.map((h) => (
                <TableCell key={h} sx={{ color: "primary.contrastText", fontWeight: 700 }}>{h}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={TABLE_HEADERS.length} align="center" sx={{ py: 4 }}>
                  <CircularProgress size={28} aria-label="Loading deleted accounts" />
                </TableCell>
              </TableRow>
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={TABLE_HEADERS.length} align="center" sx={{ py: 4, color: "text.secondary" }}>
                  No inactive account requests for the current criteria.
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row, idx) => {
                const accountUserId = getAccountUserId(row);
                const status = getAccountStatus(row);
                const editAction = getEditAction(status);
                const deleteAction = getDeleteAction(status);
                const canAct = Boolean(accountUserId) && !actionLoading;

                return (
                  <TableRow key={accountUserId ?? idx}>
                    <TableCell>{page * rowsPerPage + idx + 1}</TableCell>
                    <TableCell>{row.name ?? "—"}</TableCell>
                    <TableCell>{row.email ?? "—"}</TableCell>
                    <TableCell>{row.reason ?? "—"}</TableCell>
                    <TableCell>{status || "—"}</TableCell>
                    <TableCell>{formatDisplayDateTime(row.created_at ?? row.createdAt)}</TableCell>
                    <TableCell>
                      {formatDisplayDateTime(row.scheduled_deletion_at) === "—"
                        ? "—"
                        : formatDisplayDateTime(row.scheduled_deletion_at)}
                    </TableCell>
                    <TableCell>
                      {editAction || deleteAction ? (
                        <Stack direction="row" spacing={0.5} alignItems="center">
                          {[editAction, deleteAction]
                            .filter(Boolean)
                            .map((action) => {
                              const props = getActionButtonProps(action, status);
                              if (!props) return null;
                              const { tooltip, Icon, color, ariaLabel } = props;
                              const onClick =
                                action === editAction
                                  ? () => handleEditClick(row)
                                  : () => handleDeleteClick(row);

                              return (
                                <Tooltip key={action} title={tooltip}>
                                  <span>
                                    <IconButton
                                      size="small"
                                      color={color}
                                      aria-label={ariaLabel}
                                      disabled={!canAct}
                                      onClick={onClick}
                                    >
                                      <Icon fontSize="small" />
                                    </IconButton>
                                  </span>
                                </Tooltip>
                              );
                            })}
                        </Stack>
                      ) : (
                        "—"
                      )}
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
          <TableFooter>
            <TableRow>
              <TablePagination
                rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
                count={totalRecords}
                page={page}
                onPageChange={(_, p) => setPage(p)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(e) => {
                  setRowsPerPage(parseInt(e.target.value, 10));
                  setPage(0);
                }}
                disabled={loading || actionLoading}
                colSpan={TABLE_HEADERS.length}
                sx={{ border: 0 }}
              />
            </TableRow>
          </TableFooter>
        </Table>
      </TableContainer>

      <ConfirmationDialog
        open={confirmOpen}
        title={confirmCopy.title}
        message={confirmCopy.message}
        onClose={() => {
          if (!actionLoading) {
            setConfirmOpen(false);
            setPendingAction(null);
            setPendingRow(null);
          }
        }}
        onConfirm={() => runAction(pendingRow, pendingAction)}
      />
    </Box>
  );
}
